/** SUNTRONIX ONE — Advanced Rule Studio
 *
 *  A governed WHEN / IF / THEN builder for VALIDATION / APPROVAL /
 *  ASSIGNMENT / NOTIFICATION rules.  Sits inside Configuration Studio.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Play, Plus, RefreshCw, ArrowLeft, AlertTriangle,
  UserCog, XCircle,
} from "lucide-react";

const MODULES = ["quotation", "customer", "customer_po", "one_order",
                                  "vendor_po", "procurement", "invoice",
                                  "vendor_registration", "deployment",
                                  "recovery", "support"];

const KIND_TONE = {
  VALIDATION:  "bg-amber-50 text-amber-800 border-amber-200",
  APPROVAL:    "bg-emerald-50 text-emerald-800 border-emerald-200",
  ASSIGNMENT:  "bg-sky-50 text-sky-800 border-sky-200",
  NOTIFICATION:"bg-indigo-50 text-indigo-800 border-indigo-200",
};

const QUOTATION_DISCOUNT_STARTER = {
  module: "quotation", kind: "APPROVAL",
  name: "High Discount → Management Approval",
  description: "When a quotation is being issued with discount > 10%, require Management approval.",
  when: "QUOTE_ISSUED",
  condition: { field: "discount_percent", op: ">", value: 10 },
  approval_kind: "SINGLE",
  levels: [{ order: 1, label: "Management",
                        approvers: [{ role: "management" }],
                        target_hours: 4 }],
  priority: 50, allow_self_approval: false, target_hours: 4,
};

const HIGH_VALUE_STARTER = {
  module: "quotation", kind: "APPROVAL",
  name: "High-Value Quote (> ₹5L)",
  description: "Value gate — approval by Commercial then Management.",
  when: "QUOTE_ISSUED",
  condition: { field: "grand_total_inr", op: ">", value: 500000 },
  approval_kind: "SEQUENTIAL",
  levels: [
    { order: 1, label: "Commercial", approvers: [{ role: "commercial" }], target_hours: 6 },
    { order: 2, label: "Management", approvers: [{ role: "management" }], target_hours: 4 },
  ],
  priority: 40, allow_self_approval: false, target_hours: 24,
};

const CUST_PO_VARIANCE_STARTER = {
  module: "customer_po", kind: "VALIDATION",
  name: "Customer PO differs from accepted Quote",
  description: "Flags PO totals or currency that differ from the accepted quote.",
  condition: { or: [
    { field: "po_total_variance_pct", op: ">", value: 0.5 },
    { field: "currency_matches_quote", op: "==", value: false },
  ] },
  action: { kind: "REVIEW_REQUIRED", route: "commercial" },
  priority: 60,
};

const SEZ_STARTER = {
  module: "quotation", kind: "APPROVAL",
  name: "SEZ / GST override — Finance approval",
  description: "Any SEZ treatment or GST override routes to Finance.",
  when: "QUOTE_ISSUED",
  condition: { or: [
    { field: "sez_transaction", op: "==", value: true },
    { field: "gst_override", op: "==", value: true },
  ] },
  approval_kind: "SINGLE",
  levels: [{ order: 1, label: "Finance",
                        approvers: [{ role: "finance" }], target_hours: 8 }],
  priority: 30, allow_self_approval: false, tax_sensitive: true, target_hours: 8,
};

const VENDOR_PO_THRESHOLD_STARTER = {
  module: "vendor_po", kind: "APPROVAL",
  name: "Vendor PO > ₹10L → Procurement + Management",
  when: "VENDOR_PO_CREATED",
  condition: { field: "grand_total_inr", op: ">", value: 1000000 },
  approval_kind: "SEQUENTIAL",
  levels: [
    { order: 1, label: "Procurement", approvers: [{ role: "procurement" }], target_hours: 8 },
    { order: 2, label: "Management", approvers: [{ role: "management" }], target_hours: 6 },
  ],
  priority: 40, allow_self_approval: false, target_hours: 14,
};

const STARTERS = [QUOTATION_DISCOUNT_STARTER, HIGH_VALUE_STARTER,
                                CUST_PO_VARIANCE_STARTER, SEZ_STARTER,
                                VENDOR_PO_THRESHOLD_STARTER];


export default function RuleStudio() {
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [module, setModule] = useState(sp.get("module") || "quotation");
  const [rows, setRows] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("rules");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/rules/meta").then(({ data }) => setMeta(data)); }, []);

  const load = useCallback(async () => {
    try {
      const [rl, cf] = await Promise.all([
        api.get("/rules", { params: { module } }),
        api.get("/rules/conflicts", { params: { module } }),
      ]);
      setRows(rl.data.items || []);
      setConflicts(cf.data.conflicts || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [module]);
  useEffect(() => { load(); }, [load]);

  const canAdmin = meta?.current_user?.can_admin;
  const selected = rows.find((r) => r.id === selectedId);

  const seed = async () => {
    setBusy(true);
    try {
      for (const s of STARTERS) {
        if (s.module !== module) continue;
        await api.post("/rules", s);
      }
      toast.success("Starter rules created (DRAFT)");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    finally { setBusy(false); }
  };

  const publish = async (rid) => {
    if (!window.confirm("Publish this rule? Previous ACTIVE version for the same name will be deactivated.")) return;
    try {
      await api.post(`/rules/${rid}/publish`);
      toast.success("Published");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };

  const deactivate = async (rid) => {
    try {
      await api.post(`/rules/${rid}/deactivate`);
      toast.success("Deactivated");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clone = async (rid) => {
    try {
      const { data } = await api.post(`/rules/${rid}/clone`);
      toast.success(`Cloned as v${data.rule.version} DRAFT`);
      setSelectedId(data.rule.id);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="rule-studio">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-800 text-white grid place-items-center">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xl font-heading font-semibold">Advanced Rule Studio</div>
            <div className="text-[11px] text-slate-500">
              Validation · Approval · Assignment · Notification — WHEN / IF / THEN
            </div>
          </div>
        </div>
        <Link to="/administration/config-studio"
            className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Configuration Studio
        </Link>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <select value={module} onChange={(e) => setModule(e.target.value)}
            className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5"
            data-testid="rule-module">
          {MODULES.map((m) => <option key={m}>{m}</option>)}
        </select>
        {canAdmin && (
          <button onClick={seed} disabled={busy}
              data-testid="rule-seed"
              className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
            <Plus className="h-3 w-3" /> Seed starter rules
          </button>
        )}
        <div className="ml-auto text-[11px] text-slate-500">
          {rows.length} rules · {rows.filter((r) => r.status === "ACTIVE").length} active
        </div>
      </div>

      {conflicts.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-[11px]"
            data-testid="rule-conflicts">
          <div className="flex items-center gap-1 font-semibold text-amber-900 mb-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Conflicts detected
          </div>
          {conflicts.map((c, i) => (
            <div key={i} className="text-amber-900">
              {c.module} · {c.kind} · priority {c.priority}: {c.rules.length} competing rules —
              adjust priorities to resolve.
            </div>
          ))}
        </div>
      )}

      <div className="border-b border-slate-200 flex gap-1">
        {[["rules", "Rules"], ["test", "Test rule"], ["requests", "Approval requests"],
          ["delegations", "Delegations"]].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)}
              data-testid={`rule-tab-${k}`}
              className={"px-3 py-1.5 text-[12px] -mb-px border-b-2 " +
                              (tab === k ? "border-emerald-800 text-emerald-900 font-medium"
                                                        : "border-transparent text-slate-500")}>
            {v}
          </button>
        ))}
      </div>

      {tab === "rules" && (
        <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
          <table className="min-w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
              <tr>
                <th className="p-2 text-left">Rule</th>
                <th className="p-2 text-left">Kind</th>
                <th className="p-2 text-left">Priority</th>
                <th className="p-2 text-left">Status / v</th>
                <th className="p-2 text-left">Summary</th>
                <th className="p-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100"
                    data-testid={`rule-row-${r.id}`}>
                  <td className="p-2 font-semibold">{r.name}</td>
                  <td className="p-2">
                    <span className={"text-[9px] px-1.5 py-0.5 rounded ring-1 " + KIND_TONE[r.kind]}>
                      {r.kind}
                    </span>
                  </td>
                  <td className="p-2 font-mono">{r.priority}</td>
                  <td className="p-2 font-mono">{r.status} · v{r.version}</td>
                  <td className="p-2 text-slate-600 font-mono text-[10px]">
                    {ruleSummary(r)}
                  </td>
                  <td className="p-2 text-right space-x-1">
                    {canAdmin && r.status === "DRAFT" && (
                      <button onClick={() => publish(r.id)}
                          data-testid={`rule-publish-${r.id}`}
                          className="text-[10px] px-2 py-1 rounded bg-emerald-800 text-white">
                        Publish
                      </button>
                    )}
                    {canAdmin && r.status === "ACTIVE" && (
                      <button onClick={() => deactivate(r.id)}
                          data-testid={`rule-deactivate-${r.id}`}
                          className="text-[10px] px-2 py-1 rounded border border-rose-200 text-rose-700">
                        Deactivate
                      </button>
                    )}
                    {canAdmin && r.status !== "DRAFT" && (
                      <button onClick={() => clone(r.id)}
                          data-testid={`rule-clone-${r.id}`}
                          className="text-[10px] px-2 py-1 rounded border border-slate-200">
                        Clone
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400">
                  No rules yet — {canAdmin ? "click 'Seed starter rules' to bootstrap." : ""}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {tab === "test" && <RuleTester module={module} />}
      {tab === "requests" && <RequestsList module={module} />}
      {tab === "delegations" && <DelegationsList />}
    </div>
  );
}


function ruleSummary(r) {
  if (r.kind === "APPROVAL") {
    const levels = (r.levels || []).map((lv) =>
      lv.label || (lv.approvers || []).map((a) => a.role || a.email).join(",")
    ).join(" → ");
    return `WHEN ${r.when || "…"} IF ${_cond(r.condition)} THEN ${r.approval_kind} approval by ${levels}`;
  }
  if (r.kind === "VALIDATION") {
    return `IF ${_cond(r.condition)} THEN ${r.action?.kind || "PREVENT"}`;
  }
  return `IF ${_cond(r.condition)} THEN ${r.kind}`;
}
function _cond(c) {
  if (!c) return "always";
  if (c.and) return "(" + c.and.map(_cond).join(" AND ") + ")";
  if (c.or)  return "(" + c.or.map(_cond).join(" OR ") + ")";
  if (c.empty) return `${c.empty} is empty`;
  if (c.field) return `${c.field} ${c.op || "=="} ${JSON.stringify(c.value)}`;
  return JSON.stringify(c);
}


function RuleTester({ module }) {
  const [json, setJson] = useState(JSON.stringify({
    grand_total_inr: 750000, discount_percent: 15, sez_transaction: false,
    gst_override: false, po_total_variance_pct: 0, currency_matches_quote: true,
  }, null, 2));
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    let record;
    try { record = JSON.parse(json); }
    catch { toast.error("Invalid JSON"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/rules/test", { module, record });
      setResults(data.results || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rule-tester">
      <div className="border border-slate-200 rounded-lg bg-white p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Sample record ({module})</div>
        <textarea value={json} onChange={(e) => setJson(e.target.value)}
            rows={14} data-testid="rule-test-json"
            className="w-full border border-slate-200 rounded-md px-2 py-2 font-mono text-[11px]" />
        <button onClick={run} disabled={busy}
            data-testid="rule-test-run"
            className="mt-2 w-full text-[12px] py-2 rounded-md bg-emerald-800 text-white">
          {busy ? "Testing…" : "Run against ACTIVE rules"}
        </button>
        <div className="mt-2 text-[10px] text-slate-500">
          Rule testing NEVER modifies production records.
        </div>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Results</div>
        {!results ? (
          <div className="text-[11px] text-slate-400">Run to see matches.</div>
        ) : results.length === 0 ? (
          <div className="text-[11px] text-slate-500">No ACTIVE rules matched.</div>
        ) : (
          <ul className="space-y-1">
            {results.map((r) => (
              <li key={r.rule_id} className={"p-2 rounded border text-[11px] " +
                  (r.matched ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50")}>
                <div className="font-semibold">{r.name}</div>
                <div className="text-slate-600 font-mono text-[10px]">{r.summary}</div>
                <div className="text-[10px] mt-0.5">
                  {r.matched ? "Match" : "Skipped"} · priority {r.priority}
                </div>
                {r.route && r.route.length > 0 && (
                  <div className="text-[10px] mt-1 text-emerald-900">
                    Route: {r.route.map((lv) => lv.label || (lv.approvers || []).map((a) => a.role).join(",")).join(" → ")}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function RequestsList({ module }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/rules/requests", { params: { module } })
        .then(({ data }) => setRows(data.items || []))
        .catch(() => setRows([]));
  }, [module]);
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden"
        data-testid="rule-requests">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr>
            <th className="p-2 text-left">Rule</th>
            <th className="p-2 text-left">Record</th>
            <th className="p-2 text-left">State</th>
            <th className="p-2 text-left">Level</th>
            <th className="p-2 text-left">Requested</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="p-2 font-semibold">{r.rule_name}</td>
              <td className="p-2 font-mono">{r.record_id?.slice(0, 8)}</td>
              <td className="p-2">{r.state}</td>
              <td className="p-2">L{r.current_level}/{r.levels?.length}</td>
              <td className="p-2 font-mono text-[10px]">{r.requested_at?.slice(0, 19)}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={5} className="p-6 text-center text-slate-400">
              No approval requests for {module}.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function DelegationsList() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ delegated_to: "", effective_from: "", effective_to: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/rules/delegations")
      .then(({ data }) => setRows(data.items || [])), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/rules/delegations", form);
      toast.success("Delegation created");
      setForm({ delegated_to: "", effective_from: "", effective_to: "", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const cancel = async (id) => {
    try { await api.patch(`/rules/delegations/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="rule-delegations">
      <div className="border border-slate-200 rounded-lg bg-white p-3">
        <div className="text-[11px] font-semibold text-slate-700 mb-2 flex items-center gap-1">
          <UserCog className="h-3.5 w-3.5 text-indigo-700" /> New delegation
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={form.delegated_to}
              onChange={(e) => setForm({ ...form, delegated_to: e.target.value })}
              placeholder="Substitute email"
              data-testid="deleg-to"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="date" value={form.effective_from}
              onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              data-testid="deleg-from"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input type="date" value={form.effective_to}
              onChange={(e) => setForm({ ...form, effective_to: e.target.value })}
              data-testid="deleg-until"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
          <input value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="Reason (leave, offsite, ...)"
              className="border border-slate-200 rounded-md px-2 py-1 text-[11px]" />
        </div>
        <button onClick={submit} disabled={busy || !form.delegated_to || !form.effective_from || !form.effective_to || !form.reason}
            data-testid="deleg-submit"
            className="mt-2 text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
          {busy ? "Saving…" : "Create delegation"}
        </button>
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="p-2 text-left">Delegated to</th>
              <th className="p-2 text-left">Delegated by</th>
              <th className="p-2 text-left">Effective</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-t border-slate-100" data-testid={`deleg-${d.id}`}>
                <td className="p-2 font-mono">{d.delegated_to}</td>
                <td className="p-2 font-mono">{d.delegated_by}</td>
                <td className="p-2 font-mono text-[10px]">
                  {d.effective_from?.slice(0, 10)} → {d.effective_to?.slice(0, 10)}
                </td>
                <td className="p-2">{d.status}</td>
                <td className="p-2 text-right">
                  {d.status === "ACTIVE" && (
                    <button onClick={() => cancel(d.id)}
                        data-testid={`deleg-cancel-${d.id}`}
                        className="text-[10px] px-2 py-1 rounded border border-rose-200 text-rose-700">
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-400">No delegations.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
